import { client } from 'api/baseApi.ts';
import { FirebaseErrors } from 'core/constants/enums/FirebaseErrors.ts';
import { captureDatadogErrors } from 'core/utils/datadogUtil';

const auth = client.getFirebaseAuth();
export const getFirebaseTokenWithRetries = (maxRetries = 3) =>
	new Promise((resolve, reject) => {
		const getToken = (attempt) => {
			if (attempt <= 0) {
				captureDatadogErrors('Failed to get Firebase token after maximum retries.');
				reject(new Error('Failed to get Firebase token after maximum retries.'));
			} else {
				try {
					auth.currentUser
						.getIdTokenResult(/* forceRefresh */ true)
						.then((idTokenResult) => {
							if (Object.hasOwn(idTokenResult, 'token') && idTokenResult.token?.length) {
								resolve(idTokenResult.token);
							} else {
								captureDatadogErrors(`Error getting Token. Retrying with attempt ${attempt}`, {
									email: idTokenResult?.claims?.email,
									expiry: idTokenResult?.expirationTime,
									issuedAt: idTokenResult?.issuedAtTime,
									authTime: idTokenResult?.authTime,
								});
								getToken(attempt - 1);
							}
						})
						.catch((e) => {
							captureDatadogErrors(
								`Error getting Token: ${e.message}. Retrying with attempt ${attempt}`,
								{},
								e,
							);
							getToken(attempt - 1);
						});
				} catch (error) {
					captureDatadogErrors(`Error getting Token: ${error.message}.`, {}, error);
					getToken(attempt - 1);
				}
			}
		};
		getToken(maxRetries);
	});

export const getStoredFirebaseToken = () =>
	new Promise((resolve) => {
		try {
			auth.currentUser.getIdTokenResult(false).then((idTokenResult) => {
				if (Object.hasOwn(idTokenResult, 'token') && idTokenResult.token?.length) {
					resolve(idTokenResult.token);
				} else {
					resolve(getFirebaseTokenWithRetries());
				}
			});
		} catch (e) {
			resolve(getFirebaseTokenWithRetries());
		}
	});

export const getCurrentUser = () =>
	new Promise((resolve, reject) => {
		try {
			client.auth.onAuthStateChanged(auth, (user) => {
				if (user) {
					if (!user.emailVerified && !user.isAnonymous) {
						captureDatadogErrors(`User ${user.email} not verified.`);
						reject(new Error(FirebaseErrors.EMAIL_NOT_VERIFIED));
					}
					resolve(user);
				} else {
					captureDatadogErrors('User not found.');
					reject(new Error(FirebaseErrors.USER_NOT_FOUND)); // USER NOT FOUND
				}
			});
		} catch (error) {
			captureDatadogErrors(`Error getting current user: ${error.message}.`, {}, error);
			reject(new Error(error.message));
		}
	});
export const signUp = (email, password) =>
	new Promise((resolve, reject) => {
		try {
			client.auth
				.createUserWithEmailAndPassword(auth, email, password)
				.then((userCredential) => {
					const { user } = userCredential;
					resolve(user);
				})
				.catch((error) => {
					switch (error.code) {
						case 'auth/email-already-in-use':
							captureDatadogErrors(`Email ${email} already in use.`, {}, error);
							reject(new Error(FirebaseErrors.EMAIL_ALREADY_IN_USE));
							break;
						case 'auth/invalid-email':
							captureDatadogErrors(`Invalid email ${email}.`, {}, error);
							reject(new Error(FirebaseErrors.INVALID_EMAIL));
							break;
						default:
							captureDatadogErrors(`Error signing up: ${error.message}.`, {}, error);
							reject(new Error(error.message));
							break;
					}
				});
		} catch (error) {
			captureDatadogErrors(`Error signing up: ${error.message}.`, {}, error);
			reject(new Error(error.message));
		}
	});

export const sendVerificationEmail = (user) =>
	new Promise((resolve, reject) => {
		try {
			client.auth
				.sendEmailVerification(user)
				.then(() => {
					resolve();
				})
				.catch((error) => {
					captureDatadogErrors(`Error sending verification email: ${error.message}.`, {}, error);
					reject(new Error(error.message));
				});
		} catch (error) {
			captureDatadogErrors(`Error sending verification email: ${error.message}.`, {}, error);
			reject(new Error(error.message));
		}
	});
export const signIn = (email, password) =>
	new Promise((resolve, reject) => {
		try {
			client.auth
				.signInWithEmailAndPassword(auth, email, password)
				.then((userCredential) => {
					const { user } = userCredential;
					if (!user.emailVerified) {
						client.auth.signOut(auth);
						captureDatadogErrors(`User ${user.email} email not verified. Signing out user.`);
						reject(new Error(FirebaseErrors.EMAIL_NOT_VERIFIED));
					}
					resolve(user);
				})
				.catch((error) => {
					switch (error.code) {
						case 'auth/user-not-found':
							captureDatadogErrors(`User ${email} not found.`, {}, error);
							reject(new Error(FirebaseErrors.USER_NOT_FOUND));
							break;
						case 'auth/invalid-credential':
							captureDatadogErrors(`Invalid login credentials for user ${email}.`, {}, error);
							reject(new Error(FirebaseErrors.INVALID_LOGIN_CREDENTIALS));
							break;
						case 'auth/invalid-email':
							captureDatadogErrors(`Invalid email ${email}.`, {}, error);
							reject(new Error(FirebaseErrors.INVALID_EMAIL));
							break;
						default:
							captureDatadogErrors(`Error signing in: ${error.message}.`, {}, error);
							reject(new Error(error.message));
							break;
					}
				});
		} catch (error) {
			captureDatadogErrors(`Error signing in: ${error.message}.`, {}, error);
			reject(new Error(error.message));
		}
	});

export const sendResetPasswordEmail = (email) =>
	new Promise((resolve, reject) => {
		try {
			client.auth
				.sendPasswordResetEmail(auth, email)
				.then(() => {
					resolve();
				})
				.catch((error) => {
					captureDatadogErrors(`Error sending reset password email: ${error.message}.`, {}, error);
					reject(new Error(error.message));
				});
		} catch (error) {
			captureDatadogErrors(`Error sending reset password email: ${error.message}.`, {}, error);
			reject(new Error(error.message));
		}
	});
